<template>
  <div class="header-menu">
    <div class="header-menu__primary">
      <div v-for="(item) in menuItems" :key="item.link" class="header-menu__item">
        <NuxtLink :href="item.link" class="header-menu__link"
                  :class="{ 'header-menu__link--active': isActive(item.link) }" :data-label="item.eventLabel"
                  v-analytics:header="{content: item.eventLabel}">
          {{ item.name }}
        </NuxtLink>
      </div>
    </div>
    <div class="header-menu__divider header-menu__divider--main" aria-hidden="true"/>
    <div class="header-menu__geo">
      <HeaderCity variant="menu"/>
    </div>
    <div class="header-menu__divider header-menu__divider--after-geo" aria-hidden="true"/>

    <ClinicBranchSwitcher
        v-if="authInitialized && isAuth && showBranchSwitcherInMenu && isMobileViewport"
        variant="menu"
        :title="branchSwitcherTitle"
        class="header-menu__branch-switcher"
    />

    <div v-if="authInitialized && isAuth" class="header-menu__auth-section">
      <div v-if="showAuthMenuCaption" class="header-menu__caption">Личный кабинет</div>
      <template v-for="item in authMenuItems" :key="item.link">
        <div class="header-menu__item--auth">
          <NuxtLink
              v-if="item.isDisabled"
              :to="getRestrictedSectionHref(item)"
              :class="{
            'header-menu__link--active': item.isActive,
            'header-menu__link--disabled': true
          }"
              class="header-menu__link"
          >
            <div class="header-menu__row">
              <MenuPrototypeIcon
                  v-if="isMenuPrototypeIcon(item.icon)"
                  :name="item.icon"
                  class="header-menu__icon"
              />
              <component
                  v-else-if="resolveAuthMenuIconComponent(item.icon)"
                  :is="resolveAuthMenuIconComponent(item.icon)"
                  class="header-menu__icon"
                  aria-hidden="true"
              />
              <span class="header-menu__label">
                <span>{{ item.name }}</span>
                <MenuNewBadge v-if="item.badge" :label="item.badge" :aria-label="item.badgeAria"/>
              </span>
            </div>
            <SvgoLock class="header-menu__lock" aria-hidden="true"/>
          </NuxtLink>
          <NuxtLink v-else :to="item.link" :class="{ 'header-menu__link--active': item.isActive}"
                    class="header-menu__link">
            <div class="header-menu__row">
              <MenuPrototypeIcon
                  v-if="isMenuPrototypeIcon(item.icon)"
                  :name="item.icon"
                  class="header-menu__icon"
              />
              <component
                  v-else-if="resolveAuthMenuIconComponent(item.icon)"
                  :is="resolveAuthMenuIconComponent(item.icon)"
                  class="header-menu__icon"
                  aria-hidden="true"
              />
              <span class="header-menu__label">
                <span>{{ item.name }}</span>
                <MenuNewBadge v-if="item.badge" :label="item.badge" :aria-label="item.badgeAria"/>
              </span>
            </div>
            <SvgoChevronRightMd class="header-menu__arrow" aria-hidden="true"/>
          </NuxtLink>
          <div class="header-menu__separator"/>
        </div>
      </template>
      <div class="header-menu__item--auth">
        <button type="button" class="header-menu__link header-menu__link--logout" @click="showLogoutModal"
                v-analytics:header="{content: 'logout'}">
          <div class="header-menu__row">
            <SvgoLogout class="header-menu__icon" aria-hidden="true"/>
            <span>Выйти</span>
          </div>
        </button>
      </div>
    </div>

    <template v-else-if="authInitialized">
      <button class="header-menu__auth-button" type="button" @click="showAuthForm"
              v-analytics:header="{content: 'login'}">
        <SvgoUserRound filled aria-hidden="true"/>
        Войти или зарегистрироваться
      </button>
      <HeaderMenuVetPassportBanner
          class="header-menu__vet-passport-banner"
          @click="showAuthForm"
      >
        <template #text>
          <div>
            <span class="header-menu-vet-passport-banner__title">Ветеринарный паспорт</span>
            <span> питомца уже здесь!</span>

          </div>
        </template>
        <template #image>
          <img src="/images/cat&dog.png" alt="vetcard_banner">
        </template>
      </HeaderMenuVetPassportBanner>
      <div class="header-menu__divider header-menu__divider--guest" aria-hidden="true"/>
      <div class="header-menu__guest-section">
        <div class="header-menu__caption">Для врачей и клиник</div>
        <div class="header-menu__personal-cabinet">
          <button class="header-menu__personal-cabinet-button" @click="showVetAuth">
            Личный кабинет<br>ветеринара
          </button>
          <button class="header-menu__personal-cabinet-button" @click="showClinicAuth">
            Личный кабинет<br>клиники
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {useModal} from 'vue-final-modal'
import {GAevent} from "~/utils/metrica-goals";
import type {TUserProfile} from "~/types/user";
import {
  type ComputedClinicMenuItem,
  useClinicMenuComputedItems,
} from '~/composables/useClinicMenu';
import {
  type ComputedDoctorMenuItem,
  useDoctorMenuComputedItems,
} from '~/composables/useDoctorMenu';
import {getPersonalSectionPreviewPath, type PersonalPreviewRole} from '~/utils/personalSectionPreview';
import {
  CLIENT_OWNER_MOBILE_MENU_ITEMS,
  clientOwnerOrdersBadge,
  isClientOwnerMenuItemActive,
} from '~/composables/useClientOwnerMenu';
import {isShopEnabled} from '~/composables/useShopFeature';
import {omitShopMenuItems} from '~/utils/shopFeature';
import {activeShopOrdersCountLabel} from '~/utils/shopOrder';
import MenuPrototypeIcon from '~/components/Personal/MenuPrototypeIcon.vue';
import MenuNewBadge from '~/components/UI/MenuNewBadge.vue';
import HeaderMenuVetPassportBanner from '~/components/Header/MenuVetPassportBanner.vue';
import {isMenuPrototypeIcon, type MenuPrototypeIconId} from '~/utils/personalMenuPrototypeIcons';
import {
  SvgoCalendarDays,
  SvgoChartPie,
  SvgoCreditCard,
  SvgoFirstAid,
  SvgoHouseAdd,
  SvgoListChecklist,
  SvgoMonitorPlay,
  SvgoSettings,
  SvgoTag,
  SvgoUserCardId,
  SvgoWindowCodeBlock,
} from '#components';

const FormAuthPhone = defineAsyncComponent(() => import('~/components/Modal/FormAuthPhone.vue'));
const FormAuthClinic = defineAsyncComponent(() => import('~/components/Modal/FormAuthClinic.vue'));
const FormLogout = defineAsyncComponent(() => import('~/components/Modal/FormLogout.vue'));
const ClinicBranchSwitcher = defineAsyncComponent(() => import('~/components/Clinic/BranchSwitcher.vue'));

type MenuIconId =
    | 'home'
    | 'doctors'
    | 'schedule'
    | 'services'
    | 'history'
    | 'finances'
    | 'statistics'
    | 'reviews'
    | 'partner-programs'
    | 'widget'
    | 'education'
    | 'settings'
    | 'profile'
    | 'notifications'
    | 'requisites'
    | 'integrations'
    | 'applications'
    | 'online-consultations'
    | 'showcase'
    | 'orders'
    | 'referral-program'
    ;

type FilledMenuIconId = Exclude<MenuIconId, MenuPrototypeIconId>;

interface MenuItem {
  link: string;
  name: string;
  icon?: MenuIconId;
  badge?: string;
  isHome?: boolean;
  separatorAfter?: boolean;
  requiresOnline?: boolean;
  requiresEducation?: boolean;
  requiresPublished?: boolean;
  requiresClinicVerification?: boolean;
  requiresClinicOnline?: boolean;
}

const MENU_ICON_COMPONENTS: Record<FilledMenuIconId, Component> = {
  home: SvgoHouseAdd,
  doctors: SvgoUserCardId,
  schedule: SvgoCalendarDays,
  services: SvgoListChecklist,
  history: SvgoFirstAid,
  finances: SvgoCreditCard,
  statistics: SvgoChartPie,
  'partner-programs': SvgoTag,
  widget: SvgoWindowCodeBlock,
  education: SvgoMonitorPlay,
  settings: SvgoSettings,
};

const isMobileViewport = useHeaderMobileLayout()

function resolveAuthMenuIconComponent(icon: string | undefined): Component | undefined {
  if (!icon || isMenuPrototypeIcon(icon) || !(icon in MENU_ICON_COMPONENTS)) return undefined;
  return MENU_ICON_COMPONENTS[icon as FilledMenuIconId];
}

interface ComputedMenuItem extends MenuItem {
  isActive: boolean;
  isDisabled: boolean;
  disableReason: string;
}

/** Объединение пунктов ЛК (клиент/врач/клиника) для шапки */
type AuthMenuComputedItem = (ComputedMenuItem | ComputedClinicMenuItem | ComputedDoctorMenuItem) & {
  badge?: string;
  badgeAria?: string;
};

const authStore = useAuthStore();
const router = useRouter();
const {apiQuery} = useApi();
const {isAuth, userRole} = storeToRefs(authStore);
const {activeBranch, hasMultipleBranches, isSwitching, fetchBranches} = useClinicBranches()

const showBranchSwitcherInMenu = computed(() =>
    userRole.value === 'clinic' && (hasMultipleBranches.value || isSwitching.value),
)

const showAuthMenuCaption = computed(() => userRole.value !== 'clinic')

const branchSwitcherTitle = computed(() =>
    activeBranch.value?.name?.trim() || 'Клиника',
)

const {count: activeOrderCount, refresh: refreshActiveOrderCount} = useOwnerActiveOrderCount()

onMounted(() => {
  if (userRole.value === 'clinic') {
    fetchBranches()
  }
  if (userRole.value === 'client') {
    void refreshActiveOrderCount()
  }
})

const route = useRoute();

// Track when auth has been initialized to prevent hydration mismatches
const authInitialized = ref(false);

onMounted(() => {
  authInitialized.value = true;
});

const shopEnabled = isShopEnabled()
const clientMenuItems = omitShopMenuItems(CLIENT_OWNER_MOBILE_MENU_ITEMS, shopEnabled);

const clinicMenuComputed = useClinicMenuComputedItems();
const doctorMenuComputed = useDoctorMenuComputedItems();

const authMenuItems = computed((): AuthMenuComputedItem[] => {
  const role = userRole.value ?? 'client';
  if (role === 'clinic') {
    return clinicMenuComputed.value;
  }
  if (role === 'doctor') {
    return doctorMenuComputed.value;
  }

  return clientMenuItems.map((item) => {
    const badge = item.badge ?? clientOwnerOrdersBadge(item.link, activeOrderCount.value)

    return {
      ...item,
      badge,
      badgeAria: badge && !item.badge && activeOrderCount.value
        ? activeShopOrdersCountLabel(activeOrderCount.value)
        : undefined,
      isActive: isClientOwnerMenuItemActive(route.path, item),
      isDisabled: false,
      disableReason: '',
    }
  });
});

function getRestrictedSectionHref(item: AuthMenuComputedItem): string {
  const role = userRole.value ?? 'client';
  if (role === 'doctor' || role === 'clinic') {
    const preview = getPersonalSectionPreviewPath(role as PersonalPreviewRole, item.link);
    if (preview) return preview;
  }
  if (role === 'clinic') return '/profile-clinic/edit/';
  if (role === 'doctor') return '/profile-doctor/edit/';
  return '/profile/edit/';
}

const {isShopHost, shopUrl, mainSiteUrl} = useShopHost();
const shopCatalogLink = shopUrl(useShopPaths().catalog());

const menuItems = computed(() => {
  return [
    {
      link: mainSiteUrl("/veterinars/online/"),
      name: "Ветеринары",
      eventLabel: "Vet_doctors"
    },
    {
      link: mainSiteUrl("/clinics/"),
      name: "Клиники",
      eventLabel: "Clinics"
    },
    {
      link: mainSiteUrl("/uslugi/"),
      name: "Услуги",
      eventLabel: "Uslugi"
    },
    ...(shopEnabled ? [{
      link: shopCatalogLink,
      name: "Аптека и корма",
      eventLabel: "Shop"
    }] : []),
    {
      link: mainSiteUrl("/veterinar-rating/"),
      name: "Топ врачей",
      eventLabel: "Leaderboard"
    },
    {
      link: mainSiteUrl("/blog/"),
      name: "Блог",
      eventLabel: "Blog"
    },
  ]
});

const isActive = (path: string) => {
  if (isShopHost.value) {
    return path === shopCatalogLink;
  }

  if (path === '/veterinars/online/') {
    return route.path.startsWith('/veterinars/');
  }

  return route.path.startsWith(path);
};

const {open: showModal, close} = useModal({
  component: FormAuthPhone,
  attrs: {
    async onClose(success?: boolean) {
      await close()
      if (success) {
        await getUserProfile();
        await router.push({path: '/profile/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic') {
      close();
      setTimeout(() => (role === 'doctor' ? showVetAuth() : showClinicAuth()), 100);
    },
  }
});

const showAuthForm = () => {
  GAevent({
    category: 'Header',
    action: 'click',
    label: 'Authorization',
  });
  showModal();
}

const getUserProfile = async () => {
  await apiQuery<{ data: TUserProfile }>('/client/profile/', 'GET', {})
      .then((data) => {
        authStore.setUserProfileData(data.data);
      })
}

const {open: showVetAuth, close: closeVetAuth} = useModal({
  component: FormAuthPhone,
  attrs: {
    isDoctor: true,
    onClose(success?: boolean) {
      closeVetAuth()
      if (success) {
        router.push({path: '/profile-doctor/'})
      }
    },
    onSwitchRole(role: 'doctor' | 'clinic' | 'client') {
      closeVetAuth();
      if (role === 'client') {
        setTimeout(() => showModal(), 100);
      }
    },
  }
});

const {open: showClinicAuth, close: closeClinicAuth} = useModal({
  component: FormAuthClinic,
  attrs: {
    onClose(success?: boolean) {
      closeClinicAuth()
      if (success) {
        router.push({path: '/profile-clinic/'})
      }
    },
  }
});

const {open: showLogoutModal, close: closeLogoutModal} = useModal({
  component: FormLogout,
  attrs: {
    onClose() {
      closeLogoutModal();
    },
  },
});
</script>

<style lang="scss">
.header-menu {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  gap: 20px 30px;

  @include media(lg) {
    gap: 12px 16px;
  }

  @include media(md) {
    gap: 8px 10px;
  }

  &__primary {
    display: contents;
  }

  &__row {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    font-weight: 400;
    line-height: 135%;
    gap: 8px;
    color: inherit;

    .header-menu__item--auth & {
      padding: 0;
    }
  }

  &__icon {
    color: $color-gray;
    width: 20px !important;
    height: 20px !important;
  }

  &__lock {
    flex-shrink: 0;
    align-self: flex-start;
    width: 12px !important;
    height: 12px !important;
    margin-top: 4px;
    color: $color-gray;

    path {
      fill: currentColor;
    }
  }

  &__separator {
    align-self: stretch;
    margin: 8px 12px 4px;
    border-bottom: 1px solid alpha($color-light-gray, 0.25);

    @include media(header-nav) {
      margin: 0;
    }
  }

  &__geo {
    display: none;
    flex-shrink: 0;
    width: 100%;
    margin: 0;
    align-self: stretch;

    @include media(header-nav) {
      display: block;
    }

    @include media(header-nav, min-width) {
      width: auto;
    }
  }

  &__item {
    flex-shrink: 0;

    @include media(header-nav) {
      width: 100%;
      padding-left: 12px;
    }
  }

  &__link {
    color: $color-black;
    font-size: 14px;
    white-space: nowrap;

    &--active, &:hover {
      color: $color-accent-text;
    }

    &--active:hover {
      color: $color-accent-text;
    }
  }

  @include media(header-nav) {
    align-items: stretch;
    justify-content: flex-start;
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 4px;
    box-sizing: border-box;
    padding-bottom: 16px;

    &__primary {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;
    }

    &__link {
      display: flex;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      padding: 12px;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.35;
      white-space: normal;
      text-decoration: none;
    }

    &__item--auth .header-menu__link {
      width: 100%;
    }

    &__item--auth {
      width: 100%;
    }
  }

  &__auth-section {
    gap: 4px;
    flex-direction: column;
    display: flex;
    align-items: start;
    justify-content: start;

    @include media(header-nav) {
      width: 100%;
      gap: 0;
    }

    @include media(header-nav, min-width) {
      display: none;
    }
  }

  &__branch-switcher {
    display: flex;
    justify-content: center;
    width: 100%;
    align-self: stretch;
    margin-block: 12px;

    // Выше специфичность, чем у .clinic-branch-switcher--menu { display: inline-flex }
    &.clinic-branch-switcher--menu {
      @include media(header-nav, min-width) {
        display: none;
      }
    }
  }

  &__guest-section {
    flex-direction: column;
    display: flex;
    align-items: start;
    justify-content: start;
    gap: 4px;

    @include media(header-nav) {
      width: 100%;
      gap: 12px;
      margin-top: 16px;
    }

    @include media(header-nav, min-width) {
      display: none;
    }
  }

  &__item--auth {
    width: 100%;

    .header-menu__link {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.35;
      border-radius: 0;
      transition: color 0.2s ease, background-color 0.2s ease;
      background: none;
      border: none;
      margin: 0;
      gap: 12px;
      text-decoration: none;
      outline: none;
      cursor: pointer;
      font-family: inherit;
      text-align: left;

      &--active {
        background-color: transparent;
        color: $color-accent-text;
      }

      &--disabled {
        cursor: pointer;
        color: $color-gray;
      }

      &--logout {
        color: $color-gray;
        font-weight: 400;
        padding-block: 12px;
      }

      &:hover {
        transform: none;
        color: $color-accent-text;
      }
    }

    .header-menu__row:hover {
      background: none;
      border-radius: 0;
    }
  }

  &__arrow {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
  }

  &__personal-cabinet {
    display: flex;
    gap: 4px;
    width: 100%;

    &-button {
      background-color: $color-dark-white;
      border: none;
      border-radius: 12px;
      padding: 12px;
      line-height: 135%;
      color: $color-black;
      font-weight: 400;
      cursor: pointer;
      user-select: none;
      transition: background-color 0.3s ease;
      text-align: left;
      flex-grow: 1;

      @include media(header-nav) {
        border-radius: 8px;
      }
    }
  }

  &__auth-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: $color-black;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    padding: 0;
    white-space: nowrap;

    &:hover {
      color: $color-accent-text;
    }

    svg {
      flex-shrink: 0;
      display: block;
      width: 24px;
      height: 24px;
      color: $color-accent-text;
      line-height: 0;
      margin-bottom: 0;
      fill: none;

      path,
      circle {
        fill: none;
      }
    }

    @include media(header-nav) {
      justify-content: flex-start;
      width: 100%;
      box-sizing: border-box;
      padding: 12px;
      white-space: normal;
      text-align: left;
      border-radius: 8px;
    }

    @include media(header-nav, min-width) {
      display: none;
    }
  }

  &__caption {
    margin-left: 12px;
    padding: 0 12px 4px;
    font-weight: 500;
    font-size: 12px;
    line-height: 135%;
    color: $color-gray;

    @include media(header-nav) {
      margin-left: 0;
      padding: 0 12px 8px;
    }
  }

  &__divider {
    flex-shrink: 0;
    margin: 0 12px;
    height: 1px;
    background-color: $color-dark-white;
    align-self: stretch;

    @include media(header-nav) {
      margin-inline: 0;
      margin-block: 0;
      height: 1px;
      width: 100%;
    }

    @include media(header-nav, min-width) {
      display: none;
    }
  }

  &__row:hover &__icon {
    color: $color-accent-text;
  }

  &__link--active &__icon {
    color: $color-accent-text;
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__vet-passport-banner {
    width: 100%;
  }
}
</style>
